/*
PURPOSE: Compact inline card for displaying gesture activity items

USE CASES:
- Display gestures (smile, nod, speak_to, boogie) in compact inline format
- Show sender avatar, action text, and timestamp
- Optional custom message for speak_to gestures
- Respond action for interaction
- Expandable comments section

DOMAIN BOUNDARIES:
- Renders gesture-type content items only
- Uses ~50px height vs ~200px for standard cards
- Integrates with comment system
- Should NOT handle non-gesture content types

ASSUMPTIONS:
- Content item has relatedData.gestureType
- Sender info available in relatedData.senderUser
- Gesture themes stored in relatedData.targetTheme
*/

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { ContentItem, GestureType, OriginLocation } from 'shared/types/contentTypes';
import { gestureThemes, GestureThemeKey } from 'shared/constants/gestureThemes';
import { ContentCommentSection } from '../Comments';
import { useAuthContext } from '../Auth/AuthContext/AuthContext';
import GestureUserLink from '../GestureUserLink/GestureUserLink';
import { getUserPerspective, getReturnLabel } from '../../utils/gestureUtils';
import { returnGesture as returnGestureApi } from '../../api/api';

// Helper to build navigation URL from origin location
// For users, navigate to their dancefloor (gestures tab)
const getOriginLocationUrl = (origin: OriginLocation): string | null => {
  const { entityType, entityId } = origin;
  switch (entityType) {
    case 'user':
      return `/u/${entityId}?view=gestures`;
    case 'artist':
      return `/artist/${entityId}`;
    default:
      return null;
  }
};

// Custom timestamp formatter that uses shorter text for very recent times
const formatTimestamp = (date: Date): string => {
  const secondsAgo = differenceInSeconds(new Date(), date);
  if (secondsAgo < 60) {
    return 'just now';
  }
  return formatDistanceToNow(date, { addSuffix: true });
};

interface GestureCardProps {
  item: ContentItem;
  onClick?: () => void;
  showTimestamp?: boolean;
  cardStyle?: 'transparent' | 'solid';
  forceShowComments?: boolean;
  // Current location context - if different from item.originLocation, show where it came from
  currentLocation?: OriginLocation;
  // Callback when gesture is updated (e.g., after return)
  onGestureUpdated?: (updatedGesture: ContentItem) => void;
}

// Check if we should show origin location (item is from a different location than current)
const shouldShowOriginLocation = (
  itemOrigin: OriginLocation | undefined,
  currentLoc: OriginLocation | undefined
): boolean => {
  if (!itemOrigin) return false;
  if (!currentLoc) return true; // No current location context, show origin
  // Show if entity type or ID differs
  return itemOrigin.entityType !== currentLoc.entityType ||
         itemOrigin.entityId !== currentLoc.entityId;
};

const GestureCard: React.FC<GestureCardProps> = ({
  item,
  onClick,
  showTimestamp = true,
  cardStyle = 'transparent',
  forceShowComments = false,
  currentLocation,
  onGestureUpdated
}) => {
  const navigate = useNavigate();
  const { userProfile } = useAuthContext();
  const [showComments, setShowComments] = useState(forceShowComments);
  const [isReturning, setIsReturning] = useState(false);
  // Local state to track returned status (optimistic update)
  const [localReturnedGesture, setLocalReturnedGesture] = useState<{ returnedAt: string; returnedBy: string } | null>(
    item.relatedData?.returnedGesture || null
  );

  // Handler for clicking on origin location
  const handleLocationClick = (e: React.MouseEvent, origin: OriginLocation) => {
    e.stopPropagation();
    e.preventDefault();
    const url = getOriginLocationUrl(origin);
    if (url) {
      navigate(url);
    }
  };

  // Calculate user perspective once for all UI logic
  const senderId = item.relatedData?.senderUser?.userId;
  const targetId = item.relatedData?.targetUser?.userId;
  const perspective = getUserPerspective(userProfile?.userId, senderId, targetId);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const toggleComments = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowComments(prev => !prev);
  };

  // Handle returning a gesture (smile back, nod back, etc.)
  const handleReturnGesture = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!item.contentId || isReturning || localReturnedGesture) return;

    setIsReturning(true);
    try {
      const updatedGesture = await returnGestureApi(item.contentId);
      setLocalReturnedGesture(updatedGesture.relatedData?.returnedGesture || null);
      if (onGestureUpdated) {
        onGestureUpdated(updatedGesture);
      }
    } catch (error) {
      console.error('Error returning gesture:', error);
    } finally {
      setIsReturning(false);
    }
  };

  // Determine if return button should be shown
  // Only for receiver, only for returnable gesture types, only if not already returned
  const gestureType = item.relatedData?.gestureType as GestureType | undefined;
  const returnableTypes = ['smile', 'nod', 'boogie'];
  const canReturn = perspective === 'receiver' &&
    gestureType &&
    returnableTypes.includes(gestureType) &&
    !localReturnedGesture;

  // Get return label for the button (utility handles boogie->dance_with mapping)
  const targetThemeKey = (item.relatedData?.targetTheme as GestureThemeKey) || 'default';
  const theme = gestureThemes[targetThemeKey] || gestureThemes.default;
  const { returnLabel, returnPast } = gestureType ?
    getReturnLabel(theme, gestureType) :
    { returnLabel: 'Return', returnPast: 'returned' };

  // Get gesture action text based on theme with clickable names
  const getGestureActionText = () => {
    const gestureType = item.relatedData?.gestureType as GestureType | undefined;
    const senderName = item.relatedData?.senderUser?.displayName || 'Someone';
    const targetName = item.relatedData?.targetUser?.displayName || 'someone';
    const targetThemeKey = (item.relatedData?.targetTheme as GestureThemeKey) || 'default';
    const theme = gestureThemes[targetThemeKey] || gestureThemes.default;

    // Get appropriate action text based on viewer's perspective
    // Use pastAsSender for possessive forms when viewer is the sender
    let actionText = 'sent a gesture to';
    if (gestureType) {
      const isSenderView = perspective === 'sender';
      const gestureLabels: Record<GestureType, string> = {
        smile: (isSenderView && theme.gestures.smile.pastAsSender) || theme.gestures.smile.past,
        nod: (isSenderView && theme.gestures.nod.pastAsSender) || theme.gestures.nod.past,
        speak_to: (isSenderView && theme.gestures.speak_to.pastAsSender) || theme.gestures.speak_to.past,
        boogie: (isSenderView && theme.gestures.dance_with.pastAsSender) || theme.gestures.dance_with.past
      };
      actionText = gestureLabels[gestureType] || actionText;
    }

    return (
      <>
        <GestureUserLink
          userId={senderId}
          displayName={senderName}
          currentUserId={userProfile?.userId}
          role="sender"
          perspective={perspective}
          capitalizeYou={true}
        />
        <span className="italic"> {actionText} </span>
        <GestureUserLink
          userId={targetId}
          displayName={targetName}
          currentUserId={userProfile?.userId}
          role="target"
          perspective={perspective}
          capitalizeYou={false}
        />
      </>
    );
  };

  // Get placeholder text based on perspective
  const getCommentPlaceholder = () => {
    if (perspective === 'sender') {
      return `Say something to ${item.relatedData?.targetUser?.displayName || 'them'}...`;
    } else if (perspective === 'receiver') {
      return `Speak to ${item.relatedData?.senderUser?.displayName || 'them'}...`;
    }
    return 'Join the conversation...';
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div
        className={`group py-4 px-5 transition-colors duration-200 rounded-lg ${
          cardStyle === 'solid'
            ? 'bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
            : 'hover:bg-gray-50'
        }`}
        onClick={handleClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Small avatar on left */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-pink to-coral-orange flex items-center justify-center shadow-sm">
              <span className="text-white text-sm font-bold">
                {item.relatedData?.senderUser?.displayName?.charAt(0) || item.createdBy?.charAt(0) || '?'}
              </span>
            </div>
          </div>

          {/* Content area - stacks on mobile, inline on desktop */}
          <div className="flex-1 min-w-0">
            {/* Action text + returned status inline */}
            <div className="text-base font-light text-gray-700">
              {getGestureActionText()}
              {localReturnedGesture && (
                <span className="italic text-gray-700">
                  {'. '}
                  {(() => {
                    // For receiver: just show "You [verb] back." without name (it's redundant)
                    // Strip trailing preposition for clean display
                    if (perspective === 'receiver') {
                      const cleanedPast = returnPast
                        .replace(/ with$/, '')
                        .replace(/ at$/, '')
                        .replace(/ to$/, '');
                      return `You ${cleanedPast}.`;
                    }
                    // For sender: "{Name} [returnPast] you" - but handle word order
                    const targetName = item.relatedData?.targetUser?.displayName || 'They';
                    // If returnPast ends with "back" (no preposition), insert "you" before "back"
                    // e.g., "hugged back" → "hugged you back"
                    if (returnPast.endsWith(' back')) {
                      const beforeBack = returnPast.slice(0, -5); // remove " back"
                      return `${targetName} ${beforeBack} you back.`;
                    }
                    // Otherwise append "you" at end: "stomped back with you", "laughed with you"
                    return `${targetName} ${returnPast} you.`;
                  })()}
                </span>
              )}
            </div>

            {/* Metadata row - timestamp/comments and location */}
            <div className="flex items-center justify-between mt-1">
              {showTimestamp && (
                <span className="text-xs text-gray-500">
                  {item.createdAt && formatTimestamp(new Date(item.createdAt))}
                </span>
              )}
              <div className="flex items-center gap-1">
                {/* Return gesture button - only for receiver of returnable gestures */}
                {canReturn && (
                  <>
                    <button
                      onClick={handleReturnGesture}
                      disabled={isReturning}
                      className="text-xs text-gray-500 hover:text-coral-pink transition-colors duration-200 px-2 py-1 disabled:opacity-50"
                    >
                      {isReturning ? '...' : returnLabel}
                    </button>
                    <span className="text-gray-300 text-xs">·</span>
                  </>
                )}
                <button
                  onClick={toggleComments}
                  className="text-xs text-gray-500 hover:text-coral-pink transition-colors duration-200 px-2 py-1 flex items-center gap-1"
                >
                  {item.commentStats?.totalCount && item.commentStats.totalCount > 1 ? (
                    <>
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <span>{item.commentStats.totalCount}</span>
                    </>
                  ) : (
                    gestureType === 'speak_to' ? 'Respond' : 'Say something'
                  )}
                </button>
                {/* Origin location as clickable link */}
                {shouldShowOriginLocation(item.originLocation, currentLocation) && item.originLocation && (
                  <>
                    <span className="text-gray-300 text-xs">·</span>
                    <button
                      onClick={(e) => handleLocationClick(e, item.originLocation!)}
                      className="text-xs text-gray-400 hover:text-coral-pink hover:underline transition-colors duration-200"
                    >
                      {item.originLocation.displayName}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Optional custom message */}
            {item.text && !item.text.startsWith('sent a ') && (
              <div className="mt-2 text-sm text-gray-600 italic">
                &ldquo;{item.text}&rdquo;
              </div>
            )}
          </div>
        </div>

        {/* Expandable comments section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <ContentCommentSection
              contentId={item.contentId!}
              contentType={item.contentType || 'gesture'}
              contentTitle={item.text || 'Gesture'}
              singleThreadMode={true}
              responseMode={true}
              textFirstLayout={true}
              showAuthorAttribution={true}
              compact={true}
              placeholder={getCommentPlaceholder()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GestureCard;
